import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { array, object, string } from 'yup'

import { VerificationField } from '@/gql/generated/graphql'
import { SCHOOL_CODE_BOX, SCHOOL_CODE_LENGTH } from '@/utils/constant'
import { SchoolCodeForm } from '../school-code-form'
import { Button } from '../ui/button'
import { Modal, ModalProps } from './Modal'

const schema = object({
  schoolCode: string().length(SCHOOL_CODE_LENGTH),
  verificationFields: array()
    .of(string().oneOf(Object.values(VerificationField)))
    .default([]),
  verificationMemberId: string().when('verificationFields', {
    is: (verificationFields: string[] = []) =>
      verificationFields.includes(VerificationField.MemberId),
    then: (schema) => schema.required('会員IDを選択してください'),
  }),
  verificationBirthdate: string().when('verificationFields', {
    is: (verificationFields: string[] = []) =>
      verificationFields.includes(VerificationField.Birthdate),
    then: (schema) => schema.required('生年月日を選択してください'),
  }),
})

export interface SchoolCodeRegistrationFormData {
  schoolCode: string
  verificationFields: VerificationField[]
  verificationMemberId: string
  verificationBirthdate: string
}

export type SchoolGroupData = {
  name: string
}

interface Props extends ModalProps {
  onOk: (formdata: SchoolCodeRegistrationFormData) => Promise<void>
  onRequestClose: () => void
}

export const SchoolCodeRegistrationModal = ({
  isOpen,
  onOk,
  onRequestClose,
  ...rest
}: Props): JSX.Element => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<SchoolCodeRegistrationFormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema as any),
  })

  const [validSchoolCode, setValidSchoolCode] = useState<boolean | undefined>(
    undefined
  )

  const verificationFields = watch('verificationFields') ?? []
  const requiresMemberId = verificationFields.includes(
    VerificationField.MemberId
  )
  const requiresBirthdate = verificationFields.includes(
    VerificationField.Birthdate
  )

  const [schoolCode, setSchoolCode] = useState<string[]>(
    Array(SCHOOL_CODE_BOX).fill('')
  )

  return (
    <Modal
      isOpen={isOpen}
      showCloseButton={false}
      customStyles={{
        content: {
          textAlign: 'center',
          backgroundColor: '#FFF',
          boxShadow: '0px 2px 2px 0px #00000040',
          padding: 0,
          width: '538px',
          margin: '0px 10px',
        },
      }}
      {...rest}
      onRequestClose={onRequestClose}
    >
      <form
        onSubmit={handleSubmit(onOk)}
        className="p-[20px_20px] md:w-[538px] md:p-[48px_69px]"
      >
        <SchoolCodeForm<SchoolCodeRegistrationFormData>
          className="text-left"
          control={control}
          errors={errors}
          register={register}
          setValue={setValue}
          getValues={getValues}
          reset={reset}
          schoolCode={schoolCode}
          setSchoolCode={setSchoolCode}
          validSchoolCode={validSchoolCode}
          setValidSchoolCode={setValidSchoolCode}
        />

        <Button
          variant="secondary"
          className="mx-auto mt-[20px] flex h-[48px] w-[100px] md:mt-[31px]"
          type="submit"
          disabled={
            !watch('schoolCode') ||
            !!errors.schoolCode ||
            !validSchoolCode ||
            (requiresMemberId &&
              (!!errors.verificationMemberId ||
                !watch('verificationMemberId'))) ||
            (requiresBirthdate &&
              (!!errors.verificationBirthdate ||
                !watch('verificationBirthdate')))
          }
        >
          登録する
        </Button>
      </form>
    </Modal>
  )
}
